import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { toastOnError } from '/imports/ui/utils';
import { useFindOne } from '/imports/hooks';
import { Orgs } from '/imports/api/orgs';
import { DateInput } from '/imports/ui/DateInput';
import { generateNorthshoreBills } from '/imports/api/northshoreBilling';
import { Loading } from '/imports/ui/Loading';
import { getSolarBillsBatchStatus } from '/imports/api/solarBills';

const NorthshoreBillsPageStyled = styled(Page)`
  .generated {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
  }
`;

/** Superuser page to batch-generate Northshore solar bills over a date range. */
export const NorthshoreBillsPage = () => {
  const currentUser = useTracker(() => Meteor.user());
  if (!currentUser?.isSuperUser) return <NotPermittedPage />;
  const orgId = currentUser.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [issuedDate, setIssuedDate] = useState<string>();
  const [dueDate, setDueDate] = useState<string>();
  const [error,] = useState<any>({});
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState({ total: 0, generated: 0 });
  const navigate = useNavigate();
  /** Kick off bill generation, poll batch status, then navigate to the results. */
  const generateBills = toastOnError(async () => {
    if (!startDate || !endDate || !issuedDate || !dueDate) return;
    setGenerating(true);
    const batch = Date.now();
    const interval = setInterval(async () => {
      setStatus(await getSolarBillsBatchStatus.call({ batch }));
    }, 1500);
    await generateNorthshoreBills.call({ batch, startDate, endDate, issuedDate, dueDate });
    clearInterval(interval);
    navigate(`/solar-bills?batch=${batch}`);
    setGenerating(false);
  });
  return (
    <NorthshoreBillsPageStyled>
      <PageHeading
        left={<h1>{org.name}</h1>}
      />
      <PageBody>
        <Form onSubmit={() => generateBills()}>
          <div className="field-group">
            <FormField field="startDate" error={error}>
              <label>{strings.StartDate()}</label>
              <DateInput value={startDate} onChange={(startDate) => setStartDate(startDate)} />
            </FormField>
            <FormField field="endDate" error={error}>
              <label>{strings.EndDate()}</label>
              <DateInput value={endDate} onChange={(endDate) => setEndDate(endDate)} />
            </FormField>
          </div>
          <div className="field-group">
            <FormField field="issuedDate" error={error}>
              <label>{strings.IssuedDate()}</label>
              <DateInput value={issuedDate} onChange={(issuedDate) => setIssuedDate(issuedDate)} />
            </FormField>
            <FormField field="dueDate" error={error}>
              <label>{strings.DueDate()}</label>
              <DateInput value={dueDate} onChange={(dueDate) => setDueDate(dueDate)} />
            </FormField>
          </div>
          <div className="field-group">
            <Button type="submit" disabled={!startDate || !endDate || !issuedDate || !dueDate}>{generating ? <Loading /> : strings.Submit()}</Button>
            {generating && status.total ? <span className="generated">{strings.Generated()} {status.generated} / {status.total}</span> : null}
          </div>
        </Form>
      </PageBody>
    </NorthshoreBillsPageStyled>
  );
};
